import { fromChainId, generateAssetIdFromCosmosSdkDenom } from '@shapeshiftoss/caip';
import { KnownChainIds } from '@shapeshiftoss/types';
import * as unchained from '@shapeshiftoss/unchained-client';
import { isSome } from '@shapeshiftoss/utils';
import { bech32 } from 'bech32';
import PQueue from 'p-queue';
import { ErrorHandler } from '../error/ErrorHandler';
import { CONTRACT_INTERACTION, ValidAddressResultType } from '../types';
import { toAddressNList, toRootDerivationPath } from '../utils';
import { bnOrZero } from '../utils/bignumber';
import { assertAddressNotSanctioned } from '../utils/validateAddress';
const CHAIN_ID_TO_BECH32_ADDR_PREFIX = {
    [KnownChainIds.CosmosMainnet]: 'cosmos',
    [KnownChainIds.ThorchainMainnet]: 'thor',
    [KnownChainIds.MayachainMainnet]: 'maya',
};
const CHAIN_ID_TO_BECH32_VAL_PREFIX = {
    [KnownChainIds.CosmosMainnet]: 'cosmosvaloper',
    [KnownChainIds.ThorchainMainnet]: 'thorv',
    [KnownChainIds.MayachainMainnet]: 'mayav',
};
export const assertIsValidatorAddress = (validator, chainId) => {
    if (CHAIN_ID_TO_BECH32_VAL_PREFIX[chainId] !== bech32.decode(validator).prefix) {
        throw new Error(`invalid validator address: ${validator}`);
    }
};
const transformValidator = (validator) => ({
    address: validator.address,
    moniker: validator.moniker,
    tokens: validator.tokens,
    commission: validator.commission.rate,
    apr: validator.apr,
});
export const cosmosSdkChainIds = [
    KnownChainIds.CosmosMainnet,
    KnownChainIds.ThorchainMainnet,
    KnownChainIds.MayachainMainnet,
];
export var Denoms;
(function (Denoms) {
    Denoms["rune"] = "rune";
    Denoms["uatom"] = "uatom";
    Denoms["cacao"] = "cacao";
})(Denoms || (Denoms = {}));
export class CosmosSdkBaseAdapter {
    chainId;
    coinName;
    rootBip44Params;
    supportedChainIds;
    providers;
    assetId;
    denom;
    parser;
    constructor(args) {
        this.assetId = args.assetId;
        this.chainId = args.chainId;
        this.coinName = args.coinName;
        this.rootBip44Params = args.rootBip44Params;
        this.denom = args.denom;
        this.parser = args.parser;
        this.providers = args.providers;
        this.supportedChainIds = args.supportedChainIds;
        if (!this.supportedChainIds.includes(this.chainId)) {
            throw new Error(`${this.chainId} not supported. (supported: ${this.supportedChainIds})`);
        }
    }
    getChainId() {
        return this.chainId;
    }
    getBip44Params({ accountNumber }) {
        if (accountNumber < 0)
            throw new Error('accountNumber must be >= 0');
        return { ...this.rootBip44Params, accountNumber, isChange: false, addressIndex: 0 };
    }
    async getAccount(pubkey) {
        try {
            const account = await (async () => {
                if (this.providers.http instanceof unchained.thorchain.V1Api ||
                    this.providers.http instanceof unchained.mayachain.V1Api) {
                    const data = await this.providers.http.getAccount({ pubkey });
                    const assets = data.assets.reduce((acc, asset) => {
                        try {
                            acc.push({
                                amount: asset.amount,
                                assetId: generateAssetIdFromCosmosSdkDenom(asset.denom),
                            });
                        }
                        catch { }
                        return acc;
                    }, []);
                    return {
                        ...data,
                        assets,
                        delegations: [],
                        redelegations: [],
                        undelegations: [],
                        rewards: [],
                    };
                }
                const data = await this.providers.http.getAccount({ pubkey });
                const delegations = data.delegations.map(delegation => ({
                    assetId: this.assetId,
                    amount: delegation.balance.amount,
                    validator: transformValidator(delegation.validator),
                }));
                const redelegations = data.redelegations.map(redelegation => ({
                    destinationValidator: transformValidator(redelegation.destinationValidator),
                    sourceValidator: transformValidator(redelegation.sourceValidator),
                    entries: redelegation.entries.map(entry => ({
                        assetId: this.assetId,
                        completionTime: Number(entry.completionTime),
                        amount: entry.balance,
                    })),
                }));
                const undelegations = data.unbondings.map(undelegation => ({
                    validator: transformValidator(undelegation.validator),
                    entries: undelegation.entries.map(entry => ({
                        assetId: this.assetId,
                        completionTime: Number(entry.completionTime),
                        amount: entry.balance.amount,
                    })),
                }));
                const rewards = data.rewards.map(validatorReward => ({
                    validator: transformValidator(validatorReward.validator),
                    rewards: validatorReward.rewards
                        // We only support same-denom rewards for now
                        .filter(reward => reward.denom === this.denom)
                        .map(reward => ({
                        assetId: this.assetId,
                        amount: reward.amount,
                    })),
                }));
                const assets = data.assets.reduce((acc, asset) => {
                    try {
                        acc.push({
                            amount: asset.amount,
                            assetId: generateAssetIdFromCosmosSdkDenom(asset.denom),
                        });
                    }
                    catch { }
                    return acc;
                }, []);
                return { ...data, delegations, redelegations, undelegations, rewards, assets };
            })();
            return {
                balance: account.balance,
                chainId: this.chainId,
                assetId: this.assetId,
                chain: this.getType(),
                chainSpecific: {
                    accountNumber: account.accountNumber.toString(),
                    assets: account.assets,
                    sequence: account.sequence.toString(),
                    delegations: account.delegations,
                    redelegations: account.redelegations,
                    undelegations: account.undelegations,
                    rewards: account.rewards,
                },
                pubkey: account.pubkey,
            };
        }
        catch (err) {
            return ErrorHandler(err, {
                translation: 'chainAdapters.errors.getAccount',
                options: { pubkey },
            });
        }
    }
    async getTxHistory(input) {
        try {
            const requestQueue = input.requestQueue ?? new PQueue();
            const data = await requestQueue.add(() => this.providers.http.getTxHistory({
                pubkey: input.pubkey,
                pageSize: input.pageSize,
                cursor: input.cursor,
            }));
            const txs = await Promise.all(data.txs.map(tx => {
                if (input.knownTxIds?.has(tx.txid)) {
                    return null;
                }
                return requestQueue.add(() => this.parseTx(tx, input.pubkey));
            }));
            const filteredTxs = txs.filter(isSome);
            return {
                cursor: data.cursor,
                pubkey: input.pubkey,
                transactions: filteredTxs,
            };
        }
        catch (err) {
            return ErrorHandler(err);
        }
    }
    getAmount({ account, value, fee, sendMax, validatorAction, }) {
        if (!sendMax)
            return value;
        const availableBalance = (() => {
            switch (validatorAction?.type) {
                case 'undelegate':
                case 'redelegate':
                    return bnOrZero(account.chainSpecific.delegations.find(delegation => delegation.validator.address === validatorAction.address)?.amount);
                default:
                    return bnOrZero(account.balance);
            }
        })().minus(fee);
        if (!availableBalance.isFinite() || availableBalance.lte(0)) {
            throw new Error(`not enough balance to send: ${availableBalance.toString()}`);
        }
        return availableBalance.toString();
    }
    buildTransaction(input) {
        const { account, accountNumber, msg, memo = '', chainSpecific } = input;
        const { gas, fee } = chainSpecific;
        const bip44Params = this.getBip44Params({ accountNumber });
        const unsignedTx = {
            fee: { amount: [{ amount: bnOrZero(fee).toString(), denom: this.denom }], gas },
            msg: [msg],
            signatures: [],
            memo,
        };
        const txToSign = {
            addressNList: toAddressNList(bip44Params),
            tx: unsignedTx,
            chain_id: fromChainId(this.getType()).chainReference,
            account_number: account.chainSpecific.accountNumber,
            sequence: account.chainSpecific.sequence,
        };
        return { txToSign };
    }
    async broadcastTransaction({ senderAddress, receiverAddress, hex, }) {
        try {
            await Promise.all([
                assertAddressNotSanctioned(senderAddress),
                receiverAddress !== CONTRACT_INTERACTION && assertAddressNotSanctioned(receiverAddress),
            ]);
            const txHash = await this.providers.http.sendTx({ body: { rawTx: hex } });
            return txHash;
        }
        catch (err) {
            if (err.name === 'ResponseError') {
                const response = await err.response.json();
                const match = response.message.match(/description:\s*([^,]+)$/);
                return ErrorHandler(JSON.stringify(response), {
                    translation: 'chainAdapters.errors.broadcastTransactionWithMessage',
                    options: { message: match && match[1] ? match[1].trim() : response.message },
                });
            }
            return ErrorHandler(err, {
                translation: 'chainAdapters.errors.broadcastTransaction',
            });
        }
    }
    // eslint-disable-next-line require-await
    async validateAddress(address) {
        try {
            const chain = this.getType();
            const { prefix } = bech32.decode(address);
            if (CHAIN_ID_TO_BECH32_ADDR_PREFIX[chain] !== prefix) {
                throw new Error(`invalid address ${address} for ${this.getDisplayName()}`);
            }
            return {
                valid: true,
                result: ValidAddressResultType.Valid,
            };
        }
        catch (err) {
            return { valid: false, result: ValidAddressResultType.Invalid };
        }
    }
    async subscribeTxs(input, onMessage, onError) {
        const { pubKey, accountNumber, wallet } = input;
        const bip44Params = this.getBip44Params({ accountNumber });
        const address = await this.getAddress({ accountNumber, wallet, pubKey });
        const subscriptionId = toRootDerivationPath(bip44Params);
        await this.providers.ws.subscribeTxs(subscriptionId, { topic: 'txs', addresses: [address] }, async (msg) => onMessage(await this.parseTx(msg.data, msg.address)), err => onError({ message: err.message }));
    }
    unsubscribeTxs(input) {
        if (!input)
            return this.providers.ws.unsubscribeTxs();
        const { accountNumber } = input;
        const bip44Params = this.getBip44Params({ accountNumber });
        const subscriptionId = toRootDerivationPath(bip44Params);
        this.providers.ws.unsubscribeTxs(subscriptionId, { topic: 'txs', addresses: [] });
    }
    closeTxs() {
        this.providers.ws.close('txs');
    }
    async getValidator(address) {
        try {
            if (this.providers.http instanceof unchained.thorchain.V1Api ||
                this.providers.http instanceof unchained.mayachain.V1Api)
                return;
            const validator = await this.providers.http.getValidator({ pubkey: address });
            return transformValidator(validator);
        }
        catch (err) {
            return ErrorHandler(err, {
                translation: 'chainAdapters.errors.getValidator',
            });
        }
    }
    async parseTx(tx, pubkey) {
        const { address: _, ...parsedTx } = await this.parser.parse(tx, pubkey);
        return {
            ...parsedTx,
            pubkey,
            transfers: parsedTx.transfers.map(transfer => ({
                assetId: transfer.assetId,
                from: [transfer.from],
                to: [transfer.to],
                type: transfer.type,
                value: transfer.totalValue,
            })),
        };
    }
}
